import { addDays, datesOf, isoWeekStart, weekday, type IsoDate, type Period } from './dates';
import { firstOpenDay, isClosedDay, type HolidayRule, type ShopCalendar } from './holidays';
import type { Household } from './household';
import { toCents, toEuros } from './money';
import type { Pantry } from './pantry';
import type { Requirement } from './plan';
import { bestPrice, productOf, type PackagePrice, type PriceBook } from './price-book';
import { addLot, stockFromPantry, stockToPantry, takeFromStock, type Stock } from './stock';
import type { StoreId } from './types';

export interface ShoppingItem {
  productId: string;
  storeId: StoreId;
  packages: number;
  packagePrice: number;
  regularPrice: number;
  onOffer: boolean;
  estimated: boolean;
  total: number;
}

export interface ShoppingTrip {
  date: IsoDate;
  items: ShoppingItem[];
  total: number;
}

/**
 * DE: Bedarf eines Tages vor dem ersten möglichen Einkauf, den der Vorrat nicht deckt.
 * EN: Need of a day before the first possible trip that the stock does not cover.
 */
export interface LateNeed {
  date: IsoDate;
  productId: string;
}

export interface ShoppingResult {
  trips: ShoppingTrip[];
  total: number;
  savings: number;
  leftovers: Pantry;
  late: LateNeed[];
}

/**
 * DE: Was aus erledigten Einkäufen übrig ist und in welchen Wochen schon Wein gekauft wurde.
 * EN: What is left from done trips and in which weeks wine was already bought.
 */
export interface ShoppingCarryOver {
  stock: Stock;
  wineWeeks: readonly IsoDate[];
}

export interface ShoppingContext {
  period: Period;
  household: Household;
  book: PriceBook;
  pantry: Pantry;
  staples: readonly string[];
  holidays: readonly HolidayRule[];
  /**
   * DE: Frühester erlaubter Einkaufstag (sonst der Beginn des Zeitraums); frühere Tage sind vorbei.
   * EN: Earliest allowed shopping day (otherwise the period start); earlier days have passed.
   */
  earliestTrip?: IsoDate | undefined;
  carryOver?: ShoppingCarryOver | undefined;
}

export interface Purchase {
  productId: string;
  price: PackagePrice;
  packages: number;
}

// DE: Kleine Toleranz, damit Rundungsreste wie 0,000001 keine zusätzliche Packung auslösen.
// EN: Small tolerance so that rounding leftovers like 0.000001 never trigger an extra package.
const EPSILON = 1e-9;

/**
 * DE: Ganze Packungen für eine Menge; winzige Rundungsreste lösen keine Extrapackung aus.
 * EN: Whole packages for a quantity; tiny rounding leftovers never trigger an extra package.
 */
export function packagesNeeded(quantity: number, packSize: number): number {
  return Math.max(0, Math.ceil(quantity / packSize - EPSILON));
}

function calendarOf(context: ShoppingContext): ShopCalendar {
  const { federalState, closedDates } = context.household;
  return { federalState, closedDates, holidays: context.holidays };
}

function openTripDay(candidate: IsoDate, earliest: IsoDate, calendar: ShopCalendar): IsoDate {
  for (let day = addDays(candidate, -1); day >= earliest; day = addDays(day, -1)) {
    if (!isClosedDay(day, calendar)) return day;
  }
  return firstOpenDay(candidate, calendar);
}

/**
 * DE: Einkaufstage: der erste Tag und die Einkaufswochentage. Fällt einer auf einen geschlossenen Tag,
 * wird vorher eingekauft (nicht vor `earliestTrip`), sonst am nächsten offenen Tag.
 * EN: Shopping days: the first day and the shopping weekdays. A closed one moves to the last open day
 * before it (not before `earliestTrip`), otherwise to the next open day.
 */
export function tripDates(context: ShoppingContext): IsoDate[] {
  const { period, household } = context;
  const calendar = calendarOf(context);
  const earliest = context.earliestTrip ?? period.start;
  const candidates = datesOf(period).filter(
    (date, index) => index === 0 || household.shoppingWeekdays.includes(weekday(date)),
  );
  const moved = candidates.map((date) =>
    isClosedDay(date, calendar) ? openTripDay(date, earliest, calendar) : date,
  );
  // DE: Verschobene Tage bleiben in der Reihenfolge; nur doppelte Tage fallen weg.
  // EN: Moved days keep their order; only duplicate days are dropped.
  return [...new Set(moved)];
}

/**
 * DE: Einkauf, der einen Tag versorgt: der letzte am oder vor dem Tag, sonst der erste.
 * EN: Trip that supplies a day: the latest on or before the day, otherwise the first one.
 */
export function tripFor(trips: readonly IsoDate[], date: IsoDate): IsoDate {
  return trips.filter((trip) => trip <= date).at(-1) ?? trips[0] ?? date;
}

/**
 * DE: Einen Bedarf aus dem Vorrat decken und den Rest in ganzen Packungen kaufen; Reste werden Posten.
 * EN: Cover a need from stock and buy the rest in whole packages; leftovers become lots.
 */
export function coverNeed(
  need: Requirement,
  tripDate: IsoDate,
  stock: Stock,
  context: ShoppingContext,
): { stock: Stock; purchase: Purchase | undefined } {
  const taken = takeFromStock(stock, need.productId, need.quantity, need.date);
  if (taken.missing <= EPSILON) return { stock: taken.stock, purchase: undefined };
  const product = productOf(context.book.catalog, need.productId);
  const packages = packagesNeeded(taken.missing, product.packSize);
  const leftover = packages * product.packSize - taken.missing;
  const price = bestPrice(context.book, need.productId, context.household.storeIds, tripDate);
  const nextStock =
    leftover > EPSILON ? addLot(taken.stock, product, leftover, tripDate) : taken.stock;
  return { stock: nextStock, purchase: { productId: need.productId, price, packages } };
}

/**
 * DE: Wein möglichst nah am gewohnten Höchstpreis (teuerster bis zur Grenze), sonst der günstigste.
 * EN: Wine as close as possible to the usual maximum price (priciest within the limit), else the cheapest.
 */
function winePurchase(tripDate: IsoDate, context: ShoppingContext): Purchase | undefined {
  const { bottlesPerWeek, maxBottlePrice } = context.household.wine;
  if (bottlesPerWeek <= 0) return undefined;
  const priced = [...context.book.catalog.values()]
    .filter((product) => product.tags?.includes('alcohol'))
    .map((wine) => ({
      wine,
      price: bestPrice(context.book, wine.id, context.household.storeIds, tripDate),
    }))
    .sort((a, b) => b.price.priceCents - a.price.priceCents);
  const withinLimit = priced.find((entry) => entry.price.priceCents <= toCents(maxBottlePrice));
  const chosen = withinLimit ?? priced.at(-1);
  return chosen && { productId: chosen.wine.id, price: chosen.price, packages: bottlesPerWeek };
}

function toItem(purchase: Purchase): ShoppingItem {
  const { price, packages } = purchase;
  return {
    productId: purchase.productId,
    storeId: price.storeId,
    packages,
    packagePrice: toEuros(price.priceCents),
    regularPrice: toEuros(price.regularCents),
    onOffer: price.onOffer,
    estimated: price.estimated,
    total: toEuros(price.priceCents * packages),
  };
}

function mergePurchases(purchases: readonly Purchase[]): Purchase[] {
  const merged = new Map<string, Purchase>();
  for (const purchase of purchases) {
    const key = `${purchase.productId}|${purchase.price.storeId}`;
    const packages = (merged.get(key)?.packages ?? 0) + purchase.packages;
    merged.set(key, { ...purchase, packages });
  }
  return [...merged.values()];
}

function sortItems(items: ShoppingItem[], context: ShoppingContext): ShoppingItem[] {
  const storeRank = (storeId: StoreId) => context.household.storeIds.indexOf(storeId);
  const name = (id: string) => productOf(context.book.catalog, id).name;
  return items.sort(
    (a, b) =>
      storeRank(a.storeId) - storeRank(b.storeId) ||
      name(a.productId).localeCompare(name(b.productId), 'uk'),
  );
}

function planTrip(
  tripDate: IsoDate,
  needs: readonly Requirement[],
  stock: Stock,
  context: ShoppingContext,
): { trip: ShoppingTrip; stock: Stock; wineWeek: IsoDate; late: LateNeed[] } {
  const purchases: Purchase[] = [];
  const late: LateNeed[] = [];
  let current = stock;
  for (const need of needs) {
    // DE: Ein Kauf nach dem Tag hilft nicht mehr; was der Vorrat nicht deckt, wird gemeldet.
    // EN: Buying after the day no longer helps; what the stock does not cover is reported.
    if (need.date < tripDate) {
      const taken = takeFromStock(current, need.productId, need.quantity, need.date);
      current = taken.stock;
      if (taken.missing > EPSILON) late.push({ date: need.date, productId: need.productId });
      continue;
    }
    const result = coverNeed(need, tripDate, current, context);
    current = result.stock;
    if (result.purchase) purchases.push(result.purchase);
  }
  const items = sortItems(mergePurchases(purchases).map(toItem), context);
  const total = toEuros(items.reduce((sum, item) => sum + toCents(item.total), 0));
  return {
    trip: { date: tripDate, items, total },
    stock: current,
    wineWeek: isoWeekStart(tripDate),
    late,
  };
}

/**
 * DE: Einkäufe planen: jeder Bedarf gehört zum letzten Einkauf davor, gekauft wird in ganzen Packungen
 * im günstigsten Markt, Vorräte und haltbare Reste zuerst; Wein am ersten Einkauf jeder Woche.
 * EN: Plan the shopping: each need belongs to the latest trip before it, bought in whole packages in
 * the cheapest store, pantry and usable leftovers first; wine on the first trip of each week.
 */
export function buildShopping(
  requirements: readonly Requirement[],
  context: ShoppingContext,
): ShoppingResult {
  const trips = tripDates(context);
  const needs = requirements.filter((need) => !context.staples.includes(need.productId));
  let stock: Stock = [
    ...(context.carryOver?.stock ?? []),
    ...stockFromPantry(context.pantry, context.period.start, context.book.catalog),
  ];
  const result: ShoppingTrip[] = [];
  const late: LateNeed[] = [];
  const wineWeeks = new Set<IsoDate>(context.carryOver?.wineWeeks);
  trips.forEach((tripDate, index) => {
    const nextTrip = trips[index + 1] ?? addDays(context.period.start, context.period.days);
    // DE: Der erste Einkauf prüft auch Tage davor, an denen die Läden zu waren.
    // EN: The first trip also checks earlier days on which the shops were closed.
    const window = needs.filter(
      (need) => (index === 0 || need.date >= tripDate) && need.date < nextTrip,
    );
    const planned = planTrip(tripDate, window, stock, context);
    stock = planned.stock;
    late.push(...planned.late);
    const wine = wineWeeks.has(planned.wineWeek) ? undefined : winePurchase(tripDate, context);
    wineWeeks.add(planned.wineWeek);
    result.push(wine ? withWine(planned.trip, wine, context) : planned.trip);
  });
  return { ...summarize(result, stock, context), late };
}

function withWine(trip: ShoppingTrip, wine: Purchase, context: ShoppingContext): ShoppingTrip {
  const items = sortItems([...trip.items, toItem(wine)], context);
  return { ...trip, items, total: toEuros(toCents(trip.total) + toCents(toItem(wine).total)) };
}

/**
 * DE: Summe und Ersparnis durch Angebote über alle Einkäufe.
 * EN: Total and savings from offers over all trips.
 */
export function shoppingTotals(trips: readonly ShoppingTrip[]): { total: number; savings: number } {
  const items = trips.flatMap((trip) => trip.items);
  const totalCents = items.reduce((sum, item) => sum + toCents(item.total), 0);
  const savingsCents = items
    .filter((item) => item.onOffer)
    .reduce(
      (sum, item) =>
        sum + (toCents(item.regularPrice) - toCents(item.packagePrice)) * item.packages,
      0,
    );
  return { total: toEuros(totalCents), savings: toEuros(savingsCents) };
}

function summarize(trips: ShoppingTrip[], stock: Stock, context: ShoppingContext) {
  const lastDay = addDays(context.period.start, context.period.days - 1);
  return { trips, ...shoppingTotals(trips), leftovers: stockToPantry(stock, lastDay) };
}
